import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { HYDRATED_LISTING_STALE_TIME_MS } from '@/constants/query-freshness';
import { getSearches } from '@music-vine/setlist';
import { motionGraphicsKeys } from '@/utils/queryKeys';
import { setlist } from '@/utils/typesense';

import type {
  MotionGraphics,
  SearchResponse,
  SearchResponseHit,
  SortOption,
} from '@music-vine/setlist';

// Premium opens on a single desktop-row teaser, then each "Show more premium"
// appends a full set — mirroring the music/SFX teaser→full-page window.
const FREE_TIER_PER_PAGE = 24;
const PREMIUM_TEASER_SIZE = 4;
const PREMIUM_PAGE_SIZE = 24;

interface TierArgs {
  filter: string;
  q?: string;
  sort: SortOption;
}

export interface MotionGraphicsTierResult {
  fetchNextPage: () => void;
  hasNextPage: boolean;
  hits: SearchResponseHit<MotionGraphics>[];
  isError: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
}

type TierPage = {
  expectedSize: number;
  hits: SearchResponseHit<MotionGraphics>[];
  page: number;
};

// Offset paging so premium's small first page (teaser) stays contiguous with
// the full pages that follow.
export const tierPageWindow = (isPremium: boolean, page: number) => {
  if (!isPremium) {
    return {
      perPage: FREE_TIER_PER_PAGE,
      offset: (page - 1) * FREE_TIER_PER_PAGE,
    };
  }

  return page === 1
    ? { perPage: PREMIUM_TEASER_SIZE, offset: 0 }
    : {
        perPage: PREMIUM_PAGE_SIZE,
        offset: PREMIUM_TEASER_SIZE + (page - 2) * PREMIUM_PAGE_SIZE,
      };
};

// Each tier is its own `is_premium`-filtered search so the split stays correct
// on every sort (mirrors the music/SFX split).
export const useFreeFirstMotionGraphicsTier = (
  isPremium: boolean,
  { q, filter, sort }: TierArgs
): MotionGraphicsTierResult => {
  const baseSearch = useMemo(() => {
    const [premiumQuery, freeQuery] = getSearches({
      q,
      filter_by: filter,
      sort,
      fetchFreeAndPremium: true,
      isHomePage: true,
      featuredPageOne: false,
      // getSearches halves perPage across the free+premium pair; *2 undoes it.
      perPage: (isPremium ? PREMIUM_TEASER_SIZE : FREE_TIER_PER_PAGE) * 2,
    });

    return isPremium ? premiumQuery : freeQuery;
  }, [q, filter, sort, isPremium]);

  const query = useInfiniteQuery({
    queryKey: motionGraphicsKeys.search([baseSearch]),
    queryFn: async ({
      pageParam,
    }: {
      pageParam: number;
    }): Promise<TierPage> => {
      const { perPage, offset } = tierPageWindow(isPremium, pageParam);
      // Typesense ignores `offset` when `page` is set, so drop the builder's page.
      const { page: _page, ...search } = baseSearch;
      const { results } = await setlist.multiSearch<MotionGraphics[]>(
        { searches: [{ ...search, per_page: perPage, offset }] },
        {
          use_cache: true,
        }
      );

      const [tierResult] = results as SearchResponse<MotionGraphics>[];

      return {
        hits: tierResult?.hits ?? [],
        page: pageParam,
        expectedSize: perPage,
      };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: TierPage) =>
      lastPage.hits.length >= lastPage.expectedSize
        ? lastPage.page + 1
        : undefined,
    staleTime: HYDRATED_LISTING_STALE_TIME_MS,
    placeholderData: keepPreviousData,
  });

  const hits = useMemo(
    () => (query.data?.pages ?? []).flatMap((page) => page.hits),
    [query.data]
  );

  return {
    hits,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: !!query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    isLoading: query.isLoading,
    isError: query.isError,
  };
};
